import React, { useContext, useEffect, useRef, useState } from 'react';
import { Modal, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { GlobalContext } from 'providers/GlobalProvider';
import {
  usePreviewEntitiesStatusFromTo,
  useUpdateEntitiesStatusFromTo,
} from '../../../api/bitedb/entities/entities.hooks';
import { useUpdateProductsStatusFromTo } from '../../../api/bitedb/products/products.hooks';
import { useUpdateTariffsStatusFromTo } from '../../../api/bitedb/tariffs/tariffs.hooks';
import ConfirmStatusChangeModal from './ConfirmStatusChangeModal';
import {
  buildEntityPreviewConfirmDescription,
  resolveEntityBulkTab,
} from './bulkStatusTransitionUx';

interface BulkStatusTransitionModalProps {
  open: boolean;
  onClose: () => void;
  databaseId: string;
  databaseType: string;
  selectedItems?: any[];
  activeTab?: string;
}

type PendingEntityTransition = {
  databaseId: string;
  scope: string;
  toStatus: string;
  fromStatus?: string;
  entityIds?: string[];
  tab?: string;
  tabLabel: string;
  riskLevel?: string;
};

const STATUS_OPTIONS = [
  // { value: 'new', label: 'New' },
  { value: 'not_reviewed', label: 'Not Reviewed' },
  { value: 'cleared', label: 'Cleared' },
  { value: 'quarantined', label: 'Quarantined' },
  // { value: 'removed', label: 'Removed' },
  { value: 'barred', label: 'Barred' },
];

const RISK_LEVEL_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'unknown', label: 'Unknown / not assessed' },
];

const selectBusyStyle: React.CSSProperties = {
  width: '100%',
  paddingRight: '40px',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage:
    'url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%23cecfd2%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E")',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 16px center',
};

export const BulkStatusTransitionModal: React.FC<
  BulkStatusTransitionModalProps
> = ({
  open,
  onClose,
  databaseId,
  databaseType,
  selectedItems = [],
  activeTab,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const selectedTeamId = cur_user?.selected_team?.id;
  const { enqueueSnackbar } = useSnackbar();
  const [fromStatus] = useState('');
  const [toStatus, setToStatus] = useState('');
  const [scope, setScope] = useState('all');
  const [riskLevel, setRiskLevel] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmDetails, setConfirmDetails] = useState<{
    count?: number;
    description?: string;
    confirmDisabled?: boolean;
  }>({});
  const [pendingEntityTransition, setPendingEntityTransition] = useState<
    PendingEntityTransition | null
  >(null);
  const modalOpenRef = useRef(false);

  const isEntityDatabase =
    databaseType !== 'products' && databaseType !== 'tariffs';
  const resolvedTab = resolveEntityBulkTab(activeTab);
  const hideBulkScopes = isEntityDatabase && resolvedTab === 'Barred';
  const effectiveScope = hideBulkScopes ? 'selection' : scope;

  const updateEntitiesMutation = useUpdateEntitiesStatusFromTo(
    databaseId,
    selectedTeamId,
  );
  const updateProductsMutation = useUpdateProductsStatusFromTo(databaseId);
  const updateTariffsMutation = useUpdateTariffsStatusFromTo(databaseId);
  const previewEntitiesMutation = usePreviewEntitiesStatusFromTo(
    selectedTeamId,
  );

  const clearConfirmState = () => {
    setShowConfirmModal(false);
    setConfirmDetails({});
    setPendingEntityTransition(null);
  };

  const dismissBulkModal = () => {
    modalOpenRef.current = false;
    previewEntitiesMutation.reset();
    clearConfirmState();
    onClose();
  };

  useEffect(() => {
    modalOpenRef.current = open;
    if (open) {
      setScope(
        hideBulkScopes || selectedItems.length > 0 ? 'selection' : 'all',
      );
      setToStatus('');
      setRiskLevel('');
      clearConfirmState();
      return;
    }
    previewEntitiesMutation.reset();
    clearConfirmState();
  }, [open]);

  const isMutating =
    updateEntitiesMutation.isPending ||
    updateProductsMutation.isPending ||
    updateTariffsMutation.isPending;
  const isPreviewing = previewEntitiesMutation.isPending;
  const isBusy = isMutating || isPreviewing;

  const getStatusLabel = (value: string) =>
    STATUS_OPTIONS.find((option) => option.value === value)?.label || value;

  const getRiskLevelLabel = (value?: string) =>
    RISK_LEVEL_OPTIONS.find((option) => option.value === value)?.label ||
    value ||
    '';

  const validateInputs = () => {
    if (!toStatus) {
      enqueueSnackbar('Please select To status', { variant: 'warning' });
      return false;
    }

    if (fromStatus && fromStatus === toStatus) {
      enqueueSnackbar('From and To statuses cannot be the same', {
        variant: 'warning',
      });
      return false;
    }

    if (effectiveScope === 'selection' && selectedItems.length === 0) {
      enqueueSnackbar('Please select items to update', { variant: 'warning' });
      return false;
    }

    if (effectiveScope === 'risk_level' && !riskLevel) {
      enqueueSnackbar('Please select a risk level', { variant: 'warning' });
      return false;
    }
    return true;
  };

  const buildConfirmationDetails = () => {
    const targetLabel = getStatusLabel(toStatus);
    if (effectiveScope === 'selection') {
      const count = selectedItems.length;
      return {
        description: `This will change the status of ${count} ${
          count === 1 ? 'item' : 'items'
        } to ${targetLabel}.`,
      };
    }
    if (effectiveScope === 'zero_hits') {
      return {
        description: `This will change the status of all items with no matches to ${targetLabel}.`,
      };
    }
    return {
      description: `This will change the status of all items to ${targetLabel}.`,
    };
  };

  const executeStatusUpdate = async () => {
    if (confirmDetails.confirmDisabled) {
      return;
    }

    const callbacks = {
      onSuccess: (data: { message: string }) => {
        clearConfirmState();
        enqueueSnackbar(data.message, { variant: 'success' });
        onClose();
      },
      onError: (error: any) => {
        enqueueSnackbar(error.message || 'Failed to update entities status', {
          variant: 'error',
        });
      },
    };

    if (databaseType === 'tariffs' || databaseType === 'products') {
      const entityIds =
        effectiveScope === 'selection'
          ? selectedItems.map((item: any) => item.association_id)
          : undefined;
      const basePayload = {
        databaseId: databaseId,
        scope: effectiveScope,
        toStatus: toStatus,
        fromStatus: fromStatus || undefined,
        entityIds: entityIds,
      };
      if (databaseType === 'tariffs') {
        updateTariffsMutation.mutate(basePayload, callbacks);
        return;
      }
      updateProductsMutation.mutate(basePayload, callbacks);
      return;
    }

    if (!pendingEntityTransition) {
      return;
    }

    updateEntitiesMutation.mutate(
      {
        databaseId: pendingEntityTransition.databaseId,
        scope: pendingEntityTransition.scope,
        toStatus: pendingEntityTransition.toStatus,
        fromStatus: pendingEntityTransition.fromStatus,
        entityIds: pendingEntityTransition.entityIds,
        tab: pendingEntityTransition.tab,
        riskLevel: pendingEntityTransition.riskLevel,
      },
      callbacks,
    );
  };

  const openConfirm = (details: {
    count?: number;
    description?: string;
    confirmDisabled?: boolean;
  }) => {
    setConfirmDetails(details);
    setShowConfirmModal(true);
  };

  const requestConfirmation = () => {
    if (!validateInputs()) {
      return;
    }

    if (!isEntityDatabase) {
      openConfirm(buildConfirmationDetails());
      return;
    }

    const entityIds =
      effectiveScope === 'selection'
        ? selectedItems.map((item: any) => item.association_id)
        : undefined;

    const previewed: PendingEntityTransition = {
      databaseId: databaseId,
      scope: effectiveScope,
      toStatus: toStatus,
      fromStatus: fromStatus || undefined,
      entityIds: entityIds,
      tab:
        effectiveScope === 'all' ||
        effectiveScope === 'zero_hits' ||
        effectiveScope === 'risk_level'
          ? resolvedTab
          : undefined,
      tabLabel: resolvedTab,
      riskLevel: effectiveScope === 'risk_level' ? riskLevel : undefined,
    };

    setPendingEntityTransition(previewed);

    previewEntitiesMutation.mutate(
      {
        databaseId: previewed.databaseId,
        scope: previewed.scope,
        toStatus: previewed.toStatus,
        fromStatus: previewed.fromStatus,
        entityIds: previewed.entityIds,
        tab: previewed.tab,
        riskLevel: previewed.riskLevel,
      },
      {
        onSuccess: (preview) => {
          if (!modalOpenRef.current) {
            return;
          }
          openConfirm({
            count: preview.would_update,
            confirmDisabled: preview.would_update === 0,
            description: buildEntityPreviewConfirmDescription({
              scope: previewed.scope,
              toStatusLabel: getStatusLabel(previewed.toStatus),
              wouldUpdate: preview.would_update,
              barredExcluded: preview.barred_excluded,
              tabLabel: previewed.tabLabel,
              riskLevelLabel: getRiskLevelLabel(previewed.riskLevel),
            }),
          });
        },
        onError: (error: any) => {
          if (!modalOpenRef.current) {
            return;
          }
          setPendingEntityTransition(null);
          enqueueSnackbar(
            error.message || 'Failed to preview entities status',
            { variant: 'error' },
          );
        },
      },
    );
  };

  const handleConfirm = () => {
    executeStatusUpdate();
  };

  const handleCancelConfirm = () => {
    clearConfirmState();
  };

  return (
    <>
      <Modal open={open && !showConfirmModal} onClose={dismissBulkModal}>
        <div className='audit-add-modal-cont'>
          <div className='audit-add-modal-cont-head'>
            <div className='audit-add-modal-head-cont-change'>
              <div className='audit-add-modal-head-cont-icon'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                >
                  <path
                    d='M8 8V7M8 12.5V11.5M8 17V16M6.8 20H17.2C18.8802 20 19.7202 20 20.362 19.673C20.9265 19.3854 21.3854 18.9265 21.673 18.362C22 17.7202 22 16.8802 22 15.2V8.8C22 7.11984 22 6.27976 21.673 5.63803C21.3854 5.07354 20.9265 4.6146 20.362 4.32698C19.7202 4 18.8802 4 17.2 4H6.8C5.11984 4 4.27976 4 3.63803 4.32698C3.07354 4.6146 2.6146 5.07354 2.32698 5.63803C2 6.27976 2 7.11984 2 8.8V15.2C2 16.8802 2 17.7202 2.32698 18.362C2.6146 18.9265 3.07354 19.3854 3.63803 19.673C4.27976 20 5.11984 20 6.8 20Z'
                    stroke='#4895EF'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </div>
              <div className='audit-add-modal-head-cont-supporting'>
                <h1>Change Status</h1>
                <p>Move all items from one status to another.</p>
              </div>
            </div>
            <div
              className='audit-add-modal-head-close'
              onClick={dismissBulkModal}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
              >
                <path
                  d='M18 6L6 18M6 6L18 18'
                  stroke='#85888E'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>
          </div>
          <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

          <div
            className='audit-add-modal-cont-body'
            style={{
              paddingTop: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
          >
            {/* Select Entities Section */}
            <div className='audit-add-modal-cont-body-frame'>
              <label
                className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                style={{ display: 'block', margin: '0px' }}
              >
                Select Items
              </label>
              <select
                className='audit-add-modal-cont-body-frame-form-input'
                style={selectBusyStyle}
                value={effectiveScope}
                disabled={isBusy}
                onChange={(e) => setScope(e.target.value)}
              >
                {!hideBulkScopes && <option value='all'>All items</option>}
                {!hideBulkScopes && (
                  <option value='zero_hits'>All items with no matches</option>
                )}
                {!hideBulkScopes && isEntityDatabase && (
                  <option value='risk_level'>All items with a risk level</option>
                )}
                <option value='selection'>Selected entities</option>
              </select>
            </div>

            {effectiveScope === 'risk_level' && (
              <div className='audit-add-modal-cont-body-frame'>
                <label
                  className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                  style={{ display: 'block', margin: '0px' }}
                >
                  Risk Level
                </label>
                <select
                  value={riskLevel}
                  disabled={isBusy}
                  onChange={(e) => setRiskLevel(e.target.value)}
                  className='audit-add-modal-cont-body-frame-form-input'
                  style={selectBusyStyle}
                >
                  <option value='' disabled>
                    Select risk level
                  </option>
                  {RISK_LEVEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* To Status Section */}
            <div className='audit-add-modal-cont-body-frame'>
              <label
                className='audit-add-modal-cont-body-frame-form-row-input-label-text'
                style={{ display: 'block', margin: '0px' }}
              >
                To Status
              </label>
              <select
                value={toStatus}
                disabled={isBusy}
                onChange={(e) => setToStatus(e.target.value)}
                className='audit-add-modal-cont-body-frame-form-input'
                style={selectBusyStyle}
              >
                <option value='' disabled>
                  Select status
                </option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div
            className='activity-cont-frame-left-table-feed-row-cont-divider'
            style={{ marginTop: '16px' }}
          ></div>
          <div className='audit-modal-cont-footer'>
            <div className='audit-modal-cont-footer-content'>
              <div
                className='audit-modal-cont-footer-content-btn-cancel'
                onClick={dismissBulkModal}
              >
                <div className='audit-modal-cont-footer-content-btn-text'>
                  Cancel
                </div>
              </div>
              <div
                className='audit-modal-cont-footer-content-btn-upload'
                aria-disabled={isBusy}
                onClick={() => {
                  if (!isBusy) {
                    requestConfirmation();
                  }
                }}
              >
                <div
                  className='audit-modal-cont-footer-content-btn-text'
                  style={{ color: '#FFF' }}
                >
                  {isBusy ? (
                    <>
                      <CircularProgress
                        style={{
                          width: '30px',
                          height: '30px',
                          marginRight: '10px',
                        }}
                        color='primary'
                      />{' '}
                      {isPreviewing ? 'Checking' : 'Updating'}
                    </>
                  ) : (
                    <>Update Status</>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <Modal open={showConfirmModal} onClose={handleCancelConfirm}>
        <ConfirmStatusChangeModal
          count={confirmDetails.count}
          description={confirmDetails.description}
          onCancel={handleCancelConfirm}
          onConfirm={handleConfirm}
          isProcessing={isMutating}
          confirmDisabled={confirmDetails.confirmDisabled}
        />
      </Modal>
    </>
  );
};
